// skeleton/app/src/components/ProviderProfile/StandardsDetailsComponent.tsx

import {
    useEffect,
    useRef,
    useState,
    useCallback,
    useMemo
} from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Divider } from "primereact/divider";
import {
    alphaNumericSort,
    Measure,
    Question,
    Standard,
    ReviewStatusCode
} from "./models";
import QuestionInfoComponent from "./QuestionInfoComponent";
import QPCalculatedSection from "./ResponseComponents/QPCalcualtedSectionComponent";
import {
    Dropdown,
    DropdownChangeEvent
} from "primereact/dropdown";
import { Button } from "primereact/button";
import CommentsSidebar from "./CommentsSidebar";
import { useLocation } from "react-router";
import { useAuthContext } from "../../Context/AuthContext";
import { CheckboxChangeEvent } from "primereact/checkbox";
import { Tooltip } from "primereact/tooltip";
import httpService from "../../services/httpService";
import { ApiEndpoints } from "../../constants/appConstants";
import { useAppContext } from "../../Context";
import "primeicons/primeicons.css";
import { OverlayPanel } from "primereact/overlaypanel";
import DisputeQuestionAdditionalInfo from "../Common/DisputeQuestionAdditionalInfo/DisputeQuestionAdditionalInfo";
import SafeHtml from "../Common/SafeHtml";
import GraduateJustificationSideBar from "./GraduateScoring/GraduateScoringComponent";
import GraduatedJustificationDialog from "./GraduateScoring/GraduatedJustificationDialog";

interface StandardsDetailsComponentProps {
    standard: Standard;
    isEnabled: boolean;
    onReviewChange: (data: any) => void;
    downLoadProviderDataByDataSource: (dataSource: string) => void;
    updatePerformanceAreasForComments: (measureId: number) => void;
    providerAgencyName: string;
    evaluationStatuses: any[];
    dialogRef: any | null;
    mpi?: string;
    canDisableDropdown?: boolean;
    CanShowDisputeIcon?: boolean;
    graduatedMeasures?: any[];
    appliedTier?: string;
    isActiveReview: boolean;
    isActiveDisputeReview: boolean;
    reviewStatusCode?: string;
    reviewYear?: number;
    providerType?: string;
}

export default function StandardsDetailsComponent({
    standard,
    isEnabled,
    providerAgencyName,
    onReviewChange,
    downLoadProviderDataByDataSource,
    updatePerformanceAreasForComments,
    evaluationStatuses,
    dialogRef,
    mpi,
    canDisableDropdown,
    CanShowDisputeIcon,
    graduatedMeasures,
    appliedTier,
    isActiveReview,
    isActiveDisputeReview,
    reviewStatusCode: propReviewStatusCode,
    reviewYear: propReviewYear,
    providerType: propProviderType
}: StandardsDetailsComponentProps) {
    const LOG_PREFIX = "[StandardsDetailsComponent][SME-NA]";

    const [selectedRow, setSelectedRow] = useState<any>(null);
    const [expandedRows, setExpandedRows] = useState<any>(null);

    const [selectedReasons, setSelectedReasons] = useState<any[]>([]);
    const [graduatedReview, setGraduatedReview] = useState<any[]>([]);

    const [_checkedMeasures, setCheckedMeasures] = useState<any>({});

    const [sidebarVisible, setSidebarVisible] = useState(false);
    const [commentsSidebarKey, setCommentsSidebarKey] = useState(0);

    const smeSidebarOpenTimerRef = useRef<number | null>(null);

    const {
        appUser,
        isSmeReviewer,
        isInitialReviewer,
        isAdministrator,
        isFinalReviewer
    } = useAuthContext();

    const {
        reviewYear: contextReviewYear,
        providerType: contextProviderType
    } = useAppContext();

    const reviewYear = propReviewYear ?? contextReviewYear;
    const providerType = propProviderType ?? contextProviderType;

    const location = useLocation();
    const provider = location?.state?.provider;

    const reviewStatusCode = (
        propReviewStatusCode ||
        provider?.reviewStatusCode ||
        ""
    ).toLowerCase();

    const filteredEvaluationStatuses = useMemo(() => {
        if (isAdministrator()) {
            if (
                reviewStatusCode ===
                    ReviewStatusCode.RsSMEReview.toLowerCase() ||
                reviewStatusCode ===
                    ReviewStatusCode.ScoSMEReview.toLowerCase()
            ) {
                return evaluationStatuses.filter(
                    (option) =>
                        option.code === "a" ||
                        option.code === "na" ||
                        option.code === "sme"
                );
            }

            if (
                reviewStatusCode ===
                    ReviewStatusCode.RsFinalReview.toLowerCase() ||
                reviewStatusCode ===
                    ReviewStatusCode.ScoFinalReview.toLowerCase()
            ) {
                return evaluationStatuses.filter(
                    (option) =>
                        option.code === "a" ||
                        option.code === "na"
                );
            }
        }

        return evaluationStatuses;
    }, [
        isAdministrator,
        reviewStatusCode,
        evaluationStatuses
    ]);

    const isAdminSmeReviewMode = useMemo(() => {
        return (
            isAdministrator() &&
            [
                ReviewStatusCode.ScoSMEReview.toLowerCase(),
                ReviewStatusCode.RsSMEReview.toLowerCase(),
                ReviewStatusCode.RsResolutionSMEReview.toLowerCase(),
                ReviewStatusCode.ScoResolutionSMEReview.toLowerCase()
            ].includes(reviewStatusCode.toLowerCase())
        );
    }, [
        isAdministrator,
        reviewStatusCode
    ]);

    const [commentMeasureNumber, setCommentMeasureNumber] =
        useState("");

    const [commentMeasureId, setCommentMeasureId] =
        useState(0);

    const [commentProviderMPI, setCommentProviderMPI] =
        useState(
            location?.state?.provider?.mpi ||
            ""
        );

    const [commentLevel, setCommentLevel] =
        useState("");

    const [reasonSidebarVisible, setReasonSidebarVisible] =
        useState(false);

    const [reasonViewDialogVisible, setReasonViewDialogVisible] =
        useState(false);

    const [loadingReasons, setLoadingReasons] =
        useState(false);

    const [reasonLoadError, setReasonLoadError] =
        useState<string>("");

    const [userReason, setUserReason] =
        useState<string>("");

    const [
        currentReviewedMeasure,
        setCurrentReviewedMeasure
    ] = useState<Measure | null>(null);

    const overlay =
        useRef<OverlayPanel>(null);

    const [
        currentMeasure,
        setCurrentMeasure
    ] = useState<Measure>();

    const [
        scoringHtml,
        setScoringHtml
    ] = useState<string>("");

    const [
        scoringLoading,
        setScoringLoading
    ] = useState(false);

    const [
        isSmeSave,
        setIsSmeSave
    ] = useState(false);

    const [
        canEditJustification,
        setCanEditJustification
    ] = useState(true);

    const [
        JustificationReasons,
        setJustificationReasons
    ] = useState<any[]>();

    const activeDropdownRef =
        useRef<number | null>(null);

    const dropdownRefsMap =
        useRef<
            Record<number, Dropdown | null>
        >({});

    const tableWrapperRef =
        useRef<HTMLDivElement>(null);

    const findEvaluationStatus = (
        reviewResponse:
            | string
            | undefined
            | null
    ) => {
        return evaluationStatuses?.find(
            (option) =>
                option?.code ===
                String(
                    reviewResponse?.toLowerCase() ||
                        null
                )
        );
    };

    const filterVisibleQuestions =
        useCallback(
            (
                questions:
                    | Question[]
                    | undefined
            ) => {
                return (
                    questions?.filter(
                        (question) =>
                            !question.isCalculatedField &&
                            (!question?.displayConfig ||
                                !question
                                    ?.displayConfig
                                    ?.canHide) &&
                            question.dataFieldType &&
                            question.dataFieldType !==
                                "internal"
                    ) || []
                );
            },
            []
        );

    const isDropdownDisabled = (
        data: Measure,
        disableForSme: boolean = false
    ) => {
        if (!appUser) {
            return true;
        }

        const status =
            findEvaluationStatus(
                data?.reviewResponse
            );

        if (isAdminSmeReviewMode) {
            if (
                !data.isSmeInvolved ||
                (
                    data.reviewResponse &&
                    data.reviewResponse.toLowerCase() !==
                        "sme"
                )
            ) {
                return true;
            }
        }

        return (
            data?.isReadOnly ||
            !isEnabled ||
            status?.isDisable ||
            canDisableDropdown ||
            disableForSme
        );
    };

    const getFilteredReasons =
        useCallback(() => {
            return (
                JustificationReasons?.filter(
                    (reason) =>
                        selectedReasons.includes(
                            reason?.reasonId
                        )
                ) || []
            );
        }, [
            JustificationReasons,
            selectedReasons
        ]);

    const resetReviewState =
        useCallback(() => {
            setCurrentReviewedMeasure(
                null
            );

            setSelectedReasons([]);
            setGraduatedReview([]);
            setUserReason("");
            setJustificationReasons(
                undefined
            );
            setReasonLoadError("");
            setReasonSidebarVisible(
                false
            );
            setReasonViewDialogVisible(
                false
            );
        }, []);

    const openNotApprovedSidebar =
        useCallback(
            (
                data: Measure,
                _source: string
            ) => {
                setSelectedReasons([]);
                setGraduatedReview([]);
                setUserReason("");

                setJustificationReasons(
                    undefined
                );

                setReasonLoadError("");
                setCanEditJustification(
                    true
                );

                setCurrentReviewedMeasure(
                    data
                );

                setReasonSidebarVisible(
                    true
                );
            },
            []
        );

    const removeGraduatedReviews =
        useCallback(
            async (
                measureId: number
            ) => {
                try {
                    const data = {
                        reviewYear,
                        mpiId: mpi,
                        sourceMeasureId:
                            measureId
                    };

                    await httpService.post(
                        ApiEndpoints.RemoveGraduateReviewsBySourceMeasure,
                        data
                    );
                } catch (error) {
                    console.error(
                        `${LOG_PREFIX} Error removing graduated reviews`,
                        {
                            measureId,
                            error
                        }
                    );
                }
            },
            [
                reviewYear,
                mpi
            ]
        );

    const onReasonChange =
        useCallback(
            (
                event: CheckboxChangeEvent
            ) => {
                const value =
                    event.value ??
                    event.target?.value;

                setSelectedReasons(
                    (
                        previousReasons
                    ) => {
                        if (
                            event.checked
                        ) {
                            if (
                                previousReasons.includes(
                                    value
                                )
                            ) {
                                return previousReasons;
                            }

                            return [
                                ...previousReasons,
                                value
                            ];
                        }

                        return previousReasons.filter(
                            (
                                reasonId
                            ) =>
                                reasonId !==
                                value
                        );
                    }
                );
            },
            []
        );

    const getSelectedReasons =
        useCallback(
            async (
                event: any,
                data: Measure
            ) => {
                event.stopPropagation();

                try {
                    const response: any =
                        await httpService.get(
                            `${ApiEndpoints.GetGraduatedReviewInfo}/${reviewYear}/${mpi}/${data.measureId}`
                        );

                    const reasons =
                        response?.data
                            ?.reviewReasons
                            ?.reasons?.map(
                                (
                                    reason: any
                                ) =>
                                    reason.reasonId
                            ) || [];

                    setSelectedReasons(
                        reasons
                    );

                    setGraduatedReview(
                        response?.data
                            ?.graduatedReviewInfo ||
                            []
                    );

                    setUserReason(
                        response?.data
                            ?.reviewReasons
                            ?.additionalComment ||
                            ""
                    );

                    setCurrentReviewedMeasure(
                        data
                    );

                    setReasonViewDialogVisible(
                        true
                    );
                } catch (error) {
                    console.error(
                        `${LOG_PREFIX} Error fetching selected reasons`,
                        {
                            measureId:
                                data?.measureId,
                            measureNumber:
                                data?.measureNumber,
                            isActiveReview,
                            error
                        }
                    );
                }
            },
            [
                reviewYear,
                mpi,
                isActiveReview
            ]
        );

    const questionTemplateWithCalculatedFields =
        useCallback(
            (
                measure: Measure
            ) => {
                const calculatedQuestions =
                    measure?.questions?.filter(
                        (question) =>
                            question.isCalculatedField
                    ) || [];

                const dataFields =
                    measure?.questions?.filter(
                        (question) =>
                            !question.isCalculatedField
                    ) || [];

                return (
                    <>
                        <div className="measure-description">
                            <span>
                                <strong>
                                    Measure
                                    Description:
                                </strong>{" "}
                                <SafeHtml
                                    html={
                                        measure.measureDescription ??
                                        ""
                                    }
                                />
                            </span>

                            <br />
                            <br />
                        </div>

                        {calculatedQuestions.length >
                            0 && (
                            <div>
                                <QPCalculatedSection
                                    calculatedQuestions={
                                        calculatedQuestions
                                    }
                                    isActiveDisputeReview={
                                        isActiveDisputeReview
                                    }
                                    questionText=""
                                />
                            </div>
                        )}

                        {dataFields
                            .filter(
                                (
                                    question: Question
                                ) =>
                                    !question
                                        ?.displayConfig ||
                                    !question
                                        ?.displayConfig
                                        ?.canHide
                            )
                            .filter(
                                (
                                    question
                                ) =>
                                    question?.questionTitle
                            )
                            .map(
                                (
                                    question: Question
                                ) => (
                                    <div
                                        key={
                                            question.questionTitle
                                        }
                                        className="measure-description"
                                    >
                                        <div>
                                            {question?.questionTitle &&
                                                question.questionTitle !==
                                                    "0" &&
                                                question?.dataFieldType &&
                                                question.dataFieldType !==
                                                    "internal" && (
                                                    <h4>
                                                        Question{" "}
                                                        {
                                                            question.questionTitle
                                                        }
                                                    </h4>
                                                )}
                                        </div>

                                        {question.dataFieldType &&
                                            question.dataFieldType !==
                                                "internal" && (
                                                <p
                                                    style={{
                                                        whiteSpace:
                                                            "pre-line"
                                                    }}
                                                >
                                                    <SafeHtml
                                                        html={
                                                            question?.questionDescription ??
                                                            ""
                                                        }
                                                    />
                                                </p>
                                            )}

                                        <QuestionInfoComponent
                                            downLoadProviderDataByDataSource={
                                                downLoadProviderDataByDataSource
                                            }
                                            question={
                                                question
                                            }
                                        />

                                        {isActiveDisputeReview &&
                                            (
                                                question?.comment?.trim() ||
                                                (
                                                    Array.isArray(
                                                        question.attachments
                                                    ) &&
                                                    question
                                                        .attachments
                                                        .length >
                                                        0
                                                )
                                            ) && (
                                                <DisputeQuestionAdditionalInfo
                                                    question={
                                                        question
                                                    }
                                                />
                                            )}

                                        <Divider />
                                    </div>
                                )
                            )}
                    </>
                );
            },
            [
                isActiveDisputeReview,
                downLoadProviderDataByDataSource
            ]
        );

    const numberOfQuestionsTemplate =
        useCallback(
            (
                measure: Measure
            ) => {
                return filterVisibleQuestions(
                    measure?.questions
                ).length;
            },
            [
                filterVisibleQuestions
            ]
        );

    const requestSmeReviewCommentSidebar =
        useCallback(
            (
                data: Measure
            ) => {
                const smeMeasureSnapshot = {
                    ...data,
                    reviewResponse: "sme",
                    isSmeInvolved: true
                };

                if (
                    activeDropdownRef.current !==
                    null
                ) {
                    dropdownRefsMap.current[
                        activeDropdownRef.current
                    ]?.hide();

                    activeDropdownRef.current =
                        null;
                }

                if (
                    smeSidebarOpenTimerRef.current !==
                    null
                ) {
                    window.clearTimeout(
                        smeSidebarOpenTimerRef.current
                    );

                    smeSidebarOpenTimerRef.current =
                        null;
                }

                setCheckedMeasures(
                    (
                        previous: any
                    ) => ({
                        ...previous,
                        [data.measureNumber]:
                            "sme"
                    })
                );

                setReasonSidebarVisible(
                    false
                );

                setReasonViewDialogVisible(
                    false
                );

                setSelectedReasons([]);
                setGraduatedReview([]);
                setUserReason("");

                setJustificationReasons(
                    undefined
                );

                setReasonLoadError("");

                setCommentMeasureId(
                    data?.measureId ||
                        0
                );

                setCommentMeasureNumber(
                    data?.measureNumber ||
                        ""
                );

                setCommentLevel(
                    "measure"
                );

                setIsSmeSave(true);

                setCurrentReviewedMeasure(
                    smeMeasureSnapshot
                );

                setCommentsSidebarKey(
                    (previous) =>
                        previous + 1
                );

                setSidebarVisible(true);
            },
            []
        );

    const handleReviewChange = (
        data: Measure,
        status: string
    ) => {
        const currentStatusCode = (
            data?.reviewResponse ||
            ""
        )
            .toString()
            .toLowerCase()
            .trim();

        const newStatusCode = (
            status ||
            ""
        )
            .toString()
            .toLowerCase()
            .trim();

        if (
            currentStatusCode ===
            newStatusCode
        ) {
            return;
        }

        const isChangingFromNA =
            currentStatusCode ===
                "na" &&
            newStatusCode !==
                "na";

        const hasGraduatedReviews =
            graduatedMeasures?.some(
                (item) =>
                    item.sourceMeasureId ===
                    data.measureId
            );

        if (
            isChangingFromNA &&
            hasGraduatedReviews &&
            dialogRef?.current
        ) {
            const config = {
                open: true,
                title:
                    "Confirm Approval Status Update",
                yesButtonLabel:
                    "Update Status",
                noButtonLabel:
                    "Cancel",
                message:
                    newStatusCode ===
                    "a"
                        ? "Changing the status to Approved will remove all scoring for lower tiers. Would you like to proceed?"
                        : "Changing the status from Not Approved will remove the Not Approved status and all associated graduated reviews. Would you like to proceed?",
                onClose: async (
                    result: boolean
                ) => {
                    if (result) {
                        await removeGraduatedReviews(
                            data.measureId
                        );

                        onReviewChange(
                            {
                                reviewResponseId:
                                    data?.reviewResponseId,
                                measureId:
                                    data?.measureId,
                                reviewResponse:
                                    status
                            }
                        );
                    }

                    if (
                        dialogRef?.current
                    ) {
                        dialogRef.current.setDialogConfig(
                            undefined
                        );
                    }
                }
            };

            dialogRef.current.setDialogConfig(
                config
            );

            return;
        }

        /*
         * Selecting SME requires the comment sidebar before
         * committing the SME review response.
         */
        if (
            isInitialReviewer() &&
            newStatusCode ===
            "sme"
        ) {
            requestSmeReviewCommentSidebar(
                data
            );

            return;
        }

        setIsSmeSave(false);

        /*
         * Explicit SME -> NA case.
         * Require confirmation first, then open the
         * Not Approved justification workflow.
         */
        if (
            currentStatusCode ===
                "sme" &&
            newStatusCode ===
                "na"
        ) {
            if (
                dialogRef?.current
            ) {
                const config = {
                    open: true,
                    title:
                        "Confirm SME Status Change",
                    message: `Once you change the review status for measure ${data?.measureNumber}, you cannot update it again. Do you want to proceed?`,
                    onClose: (
                        result: boolean
                    ) => {
                        if (result) {
                            dialogRef.current?.setDialogConfig(
                                undefined
                            );

                            setTimeout(
                                () => {
                                    openNotApprovedSidebar(
                                        data,
                                        "sme-to-na"
                                    );
                                },
                                0
                            );

                            return;
                        }

                        dialogRef.current?.setDialogConfig(
                            undefined
                        );
                    }
                };

                dialogRef.current.setDialogConfig(
                    config
                );

                return;
            }

            openNotApprovedSidebar(
                data,
                "sme-to-na-no-dialog"
            );

            return;
        }

        /*
         * Non-SME reviewer selecting NA.
         */
        if (
            !isSmeReviewer() &&
            currentStatusCode !==
                "sme" &&
            newStatusCode ===
                "na"
        ) {
            openNotApprovedSidebar(
                data,
                "non-sme-na"
            );

            return;
        }

        /*
         * SME reviewer/admin changing an SME measure.
         */
        if (
            (
                isSmeReviewer() ||
                isAdministrator()
            ) &&
            currentStatusCode ===
                "sme"
        ) {
            if (
                dialogRef?.current
            ) {
                const config = {
                    open: true,
                    title:
                        "Confirm SME Status Change",
                    message: `Once you change the review status for measure ${data?.measureNumber}, you cannot update it again. Do you want to proceed?`,
                    onClose: (
                        result: boolean
                    ) => {
                        if (
                            result
                        ) {
                            if (
                                newStatusCode ===
                                "na"
                            ) {
                                dialogRef.current?.setDialogConfig(
                                    undefined
                                );

                                setTimeout(
                                    () => {
                                        openNotApprovedSidebar(
                                            data,
                                            "confirmed-sme-na"
                                        );
                                    },
                                    0
                                );

                                return;
                            }

                            onReviewChange(
                                {
                                    reviewResponseId:
                                        data.reviewResponseId,
                                    measureId:
                                        data.measureId,
                                    reviewResponse:
                                        status
                                }
                            );
                        }

                        dialogRef.current?.setDialogConfig(
                            undefined
                        );
                    }
                };

                dialogRef.current.setDialogConfig(
                    config
                );

                return;
            }

            if (
                newStatusCode ===
                "na"
            ) {
                openNotApprovedSidebar(
                    data,
                    "sme-na-no-dialog"
                );

                return;
            }
        }

        onReviewChange({
            reviewResponseId:
                data?.reviewResponseId,
            measureId:
                data?.measureId,
            reviewResponse:
                status
        });
    };

    const saveSMEResponse =
        useCallback((): void => {
            if (
                !currentReviewedMeasure ||
                !isSmeSave
            ) {
                return;
            }

            const reasons =
                getFilteredReasons();

            onReviewChange({
                reviewResponseId:
                    currentReviewedMeasure
                        ?.reviewResponseId,
                measureId:
                    currentReviewedMeasure
                        ?.measureId,
                reviewResponse:
                    "sme",
                reasons,
                userReason
            });

            setSidebarVisible(false);
            setIsSmeSave(false);

            setCurrentReviewedMeasure(
                null
            );

            setSelectedReasons([]);
            setGraduatedReview([]);
            setUserReason("");

            setJustificationReasons(
                undefined
            );

            setReasonLoadError("");
        }, [
            currentReviewedMeasure,
            isSmeSave,
            getFilteredReasons,
            userReason,
            onReviewChange
        ]);

    const saveNotApprovedResponse =
        useCallback((): void => {
            if (
                !currentReviewedMeasure
            ) {
                return;
            }

            const reasons =
                getFilteredReasons();

            onReviewChange({
                reviewResponseId:
                    currentReviewedMeasure
                        ?.reviewResponseId,
                measureId:
                    currentReviewedMeasure
                        ?.measureId,
                reviewResponse:
                    "na",
                reasons,
                userReason
            });

            resetReviewState();
        }, [
            currentReviewedMeasure,
            getFilteredReasons,
            userReason,
            onReviewChange,
            resetReviewState
        ]);

    const isSmeMeasure =
        useCallback(
            (
                measure:
                    | Measure
                    | undefined
                    | null
            ): boolean => {
                return (
                    measure?.reviewResponse?.toLowerCase() ===
                    "sme"
                );
            },
            []
        );

    const approvedCheckbox = (
        data: Measure
    ) => {
        let disableForSme =
            false;

        if (
            isSmeReviewer()
        ) {
            disableForSme =
                data.isEnabledForSme !==
                true;
        }

        const isDisabled =
            isDropdownDisabled(
                data,
                disableForSme
            );

        const stopRowSelection = (
            event: React.MouseEvent
        ) =>
            event.stopPropagation();

        const rowFilteredOptions =
            isAdminSmeReviewMode &&
            data.isSmeInvolved !==
                true
                ? filteredEvaluationStatuses.filter(
                      (
                          option
                      ) =>
                          option.code !==
                          "sme"
                  )
                : filteredEvaluationStatuses;

        const optionsList =
            isAdministrator()
                ? rowFilteredOptions
                : evaluationStatuses;

        /*
         * Local checked state allows the UI to immediately show
         * the selected SME status before the parent data refreshes.
         */
        const localStatusCode =
            _checkedMeasures?.[
                data.measureNumber
            ]
                ? String(
                      _checkedMeasures[
                          data
                              .measureNumber
                      ]
                  )
                      .toLowerCase()
                      .trim()
                : null;

        const currentStatusCode =
            localStatusCode ||
            data.reviewResponse?.toLowerCase() ||
            null;

        const selectedValue =
            optionsList.find(
                (
                    option
                ) =>
                    option.code ===
                    currentStatusCode
            ) ||
            optionsList.find(
                (
                    option
                ) =>
                    option.code ===
                    "nr"
            ) ||
            null;

        const showSmeFlag =
            (
                isInitialReviewer() &&
                currentStatusCode ===
                    "sme"
            ) ||
            (
                isSmeReviewer() &&
                data.isEnabledForSme &&
                data.isSmeInvolved
            ) ||
            (
                (
                    isFinalReviewer() ||
                    isAdministrator()
                ) &&
                data.isSmeInvolved
            );

        return (
            <div
                onClick={
                    stopRowSelection
                }
                onMouseDown={
                    stopRowSelection
                }
            >
                <Dropdown
                    ref={(
                        element
                    ) => {
                        dropdownRefsMap.current[
                            data.measureId
                        ] =
                            element;
                    }}
                    disabled={
                        isDisabled
                    }
                    value={
                        selectedValue
                    }
                    onChange={(
                        event: DropdownChangeEvent
                    ) => {
                        event.originalEvent?.stopPropagation?.();

                        if (
                            !event.value ||
                            !event.value
                                .code
                        ) {
                            return;
                        }

                        const code =
                            event.value
                                .code;

                        handleReviewChange(
                            data,
                            code
                        );

                        setCheckedMeasures(
                            (
                                previous: any
                            ) => ({
                                ...previous,
                                [data.measureNumber]:
                                    code
                            })
                        );
                    }}
                    options={
                        optionsList
                    }
                    optionLabel="name"
                    placeholder="Select Approval Status"
                    appendTo={
                        document.body
                    }
                    onShow={() => {
                        const previousId =
                            activeDropdownRef.current;

                        if (
                            previousId !==
                                null &&
                            previousId !==
                                data.measureId
                        ) {
                            dropdownRefsMap.current[
                                previousId
                            ]?.hide();
                        }

                        activeDropdownRef.current =
                            data.measureId;
                    }}
                    onHide={() => {
                        if (
                            activeDropdownRef.current ===
                            data.measureId
                        ) {
                            activeDropdownRef.current =
                                null;
                        }
                    }}
                />

                {showSmeFlag && (
                    <span
                        style={{
                            marginLeft:
                                8
                        }}
                    >
                        <i
                            className="pi pi-flag-fill"
                            style={{
                                color:
                                    "#FF9800",
                                fontSize:
                                    22,
                                verticalAlign:
                                    "middle"
                            }}
                            title="SME Review"
                        />
                    </span>
                )}

                {String(
                    data?.reviewResponse?.toLowerCase() ||
                        null
                ) ===
                    "na" && (
                    <>
                        <i
                            className="pi pi-comments measure-not-approved-icon"
                            onClick={(
                                event
                            ) => {
                                setCanEditJustification(
                                    !isDisabled
                                );

                                getSelectedReasons(
                                    event,
                                    data
                                );
                            }}
                            title="Fail Reasons"
                        />

                        <Tooltip target=".pi-comments" />
                    </>
                )}
            </div>
        );
    };

    const addCommentButton =
        useCallback(
            (
                measure: Measure
            ) => {
                const handleCommentButtonClick =
                    (
                        event: any
                    ) => {
                        event.stopPropagation();

                        setCommentMeasureId(
                            measure?.measureId
                        );

                        setCommentMeasureNumber(
                            measure?.measureNumber
                        );

                        setCommentLevel(
                            "measure"
                        );

                        setCurrentReviewedMeasure(
                            null
                        );

                        setJustificationReasons(
                            undefined
                        );

                        setSelectedReasons(
                            []
                        );

                        setGraduatedReview(
                            []
                        );

                        setUserReason("");

                        setIsSmeSave(
                            false
                        );

                        setReasonLoadError(
                            ""
                        );

                        setSidebarVisible(
                            true
                        );
                    };

                return (
                    <div className="d-flex justify-content-space-between">
                        <Button
                            onClick={
                                handleCommentButtonClick
                            }
                        >
                            Add comment
                        </Button>

                        {measure?.isCommentsAvailable ? (
                            <Button
                                severity="secondary"
                                icon="pi pi-comments"
                                outlined
                                text
                                onClick={
                                    handleCommentButtonClick
                                }
                                title="Comments"
                            />
                        ) : (
                            <div>
                                <i
                                    className="pi"
                                    style={{
                                        minWidth:
                                            "34px"
                                    }}
                                />
                            </div>
                        )}
                    </div>
                );
            },
            []
        );

    const getScoringReasonForMeasure =
        useCallback(
            async (
                row: Measure,
                event: any
            ) => {
                setCurrentMeasure(
                    row
                );

                setScoringLoading(
                    true
                );

                setScoringHtml("");

                if (
                    overlay?.current
                ) {
                    overlay.current.toggle(
                        event
                    );
                }

                httpService
                    .get(
                        `${ApiEndpoints.GetMeasureScoringCriteria}/${row?.measureId}/${standard?.standardId}/${standard?.tierId}/${reviewYear}/${providerType}`
                    )
                    .then(
                        (
                            response: any
                        ) => {
                            setScoringHtml(
                                response
                                    ?.data
                                    ?.htmlScoringCriteria ||
                                    ""
                            );
                        }
                    )
                    .catch(
                        (
                            error: any
                        ) => {
                            console.error(
                                `${LOG_PREFIX} Failed to load scoring guidance`,
                                {
                                    measureId:
                                        row?.measureId,
                                    measureNumber:
                                        row?.measureNumber,
                                    error
                                }
                            );

                            setScoringHtml(
                                "<div style='color:red;'>Failed to load scoring guidance.</div>"
                            );
                        }
                    )
                    .finally(
                        () => {
                            setScoringLoading(
                                false
                            );
                        }
                    );
            },
            [
                standard?.standardId,
                standard?.tierId,
                reviewYear,
                providerType
            ]
        );

    const getReviewReasons =
        useCallback(() => {
            if (
                !reviewYear
            ) {
                setReasonLoadError(
                    "Unable to load failed reasons because review year is missing."
                );

                return;
            }

            if (
                !currentReviewedMeasure?.measureId
            ) {
                setReasonLoadError(
                    "Unable to load failed reasons because measure information is missing."
                );

                return;
            }

            const url =
                `${ApiEndpoints.GetReviewReasons}/` +
                `${reviewYear}/` +
                `${providerType}/` +
                `${currentReviewedMeasure.measureId}/` +
                `${standard?.tierId}/` +
                `${standard?.standardId}`;

            setLoadingReasons(
                true
            );

            setReasonLoadError(
                ""
            );

            httpService
                .get(url)
                .then(
                    (
                        response: any
                    ) => {
                        const reasons =
                            response?.data ||
                            [];

                        if (
                            response?.status ===
                            200
                        ) {
                            setJustificationReasons(
                                Array.isArray(
                                    reasons
                                )
                                    ? reasons
                                    : []
                            );

                            if (
                                Array.isArray(
                                    reasons
                                ) &&
                                reasons.length ===
                                    0
                            ) {
                                setReasonLoadError(
                                    "No failed reasons were returned for this measure."
                                );
                            }
                        }
                    }
                )
                .catch(
                    (
                        error: any
                    ) => {
                        console.error(
                            `${LOG_PREFIX} Error fetching failed reasons`,
                            {
                                url,
                                reviewYear,
                                providerType,
                                measureId:
                                    currentReviewedMeasure
                                        ?.measureId,
                                measureNumber:
                                    currentReviewedMeasure
                                        ?.measureNumber,
                                tierId:
                                    standard?.tierId,
                                standardId:
                                    standard?.standardId,
                                error
                            }
                        );

                        setJustificationReasons(
                            []
                        );

                        setReasonLoadError(
                            "Failed to load failed reasons. Please try again or contact support."
                        );
                    }
                )
                .finally(
                    () => {
                        setLoadingReasons(
                            false
                        );
                    }
                );
        }, [
            reviewYear,
            providerType,
            currentReviewedMeasure?.measureId,
            currentReviewedMeasure?.measureNumber,
            standard?.tierId,
            standard?.standardId
        ]);

    useEffect(() => {
        setCommentProviderMPI(
            mpi ||
            ""
        );
    }, [
        mpi
    ]);

    useEffect(() => {
        return () => {
            if (
                smeSidebarOpenTimerRef.current !==
                null
            ) {
                window.clearTimeout(
                    smeSidebarOpenTimerRef.current
                );

                smeSidebarOpenTimerRef.current =
                    null;
            }
        };
    }, []);

    /*
     * Safety fallback:
     * If SME mode was selected but the comment sidebar
     * somehow closed during a render transition, reopen it.
     */
    useEffect(() => {
        if (
            isSmeSave &&
            currentReviewedMeasure
                ?.reviewResponse
                ?.toLowerCase() ===
                "sme" &&
            commentLevel ===
                "measure" &&
            commentMeasureId >
                0 &&
            !sidebarVisible
        ) {
            console.warn(
                `${LOG_PREFIX} SME sidebar visibility fallback triggered`,
                {
                    measureId:
                        commentMeasureId,
                    measureNumber:
                        commentMeasureNumber
                }
            );

            setCommentsSidebarKey(
                (
                    previous
                ) =>
                    previous +
                    1
            );

            setSidebarVisible(
                true
            );
        }
    }, [
        isSmeSave,
        currentReviewedMeasure,
        commentLevel,
        commentMeasureId,
        commentMeasureNumber,
        sidebarVisible
    ]);

    useEffect(() => {
        if (
            selectedRow
        ) {
            const measureId =
                selectedRow?.measureId;

            if (
                measureId !=
                null
            ) {
                setExpandedRows({
                    [measureId]:
                        true
                });
            } else {
                setExpandedRows(
                    {}
                );
            }

            setTimeout(
                () => {
                    const selectedTr =
                        tableWrapperRef.current?.querySelector(
                            "tr.p-highlight"
                        );

                    if (
                        selectedTr
                    ) {
                        selectedTr.scrollIntoView(
                            {
                                behavior:
                                    "smooth",
                                block:
                                    "start"
                            }
                        );
                    }
                },
                0
            );
        } else {
            setExpandedRows(
                null
            );
        }
    }, [
        selectedRow
    ]);

    useEffect(() => {
        if (
            currentReviewedMeasure &&
            (
                reasonSidebarVisible ||
                reasonViewDialogVisible
            )
        ) {
            getReviewReasons();
        } else if (
            !currentReviewedMeasure &&
            JustificationReasons
        ) {
            setJustificationReasons(
                undefined
            );

            setReasonLoadError(
                ""
            );
        }
    }, [
        currentReviewedMeasure,
        reasonSidebarVisible,
        reasonViewDialogVisible,
        getReviewReasons
    ]);

    const measureNameTemplate =
        useCallback(
            (
                row: Measure
            ) => {
                const localStatusCode =
                    _checkedMeasures?.[
                        row.measureNumber
                    ]
                        ? String(
                              _checkedMeasures[
                                  row
                                      .measureNumber
                              ]
                          )
                              .toLowerCase()
                              .trim()
                        : null;

                const showSmeFlag =
                    localStatusCode ===
                        "sme" ||
                    isSmeMeasure(
                        row
                    );

                return (
                    <>
                        <span>
                            {row?.measureNumber ||
                                ""}
                        </span>

                        {showSmeFlag && (
                            <span
                                style={{
                                    marginLeft:
                                        6
                                }}
                            >
                                <i
                                    className="pi pi-flag-fill"
                                    style={{
                                        color:
                                            "#FF9800",
                                        fontSize:
                                            18,
                                        verticalAlign:
                                            "middle"
                                    }}
                                    title="SME Review"
                                />
                            </span>
                        )}

                        &nbsp;

                        <button
                            className="pi pi-info-circle scoring-icon"
                            onClick={(
                                event
                            ) => {
                                event.stopPropagation();

                                getScoringReasonForMeasure(
                                    row,
                                    event
                                );
                            }}
                        />
                    </>
                );
            },
            [
                getScoringReasonForMeasure,
                isSmeMeasure,
                _checkedMeasures
            ]
        );

    const columnDisputeQuesTemplate =
        useCallback(() => {
            return CanShowDisputeIcon ? (
                <span
                    style={{
                        marginLeft:
                            8
                    }}
                >
                    <i
                        className="pi pi-flag-fill"
                        style={{
                            width:
                                "20px",
                            paddingTop:
                                "5px",
                            color:
                                "red",
                            fontSize:
                                "1.2rem"
                        }}
                        title="Dispute Measure"
                    />
                </span>
            ) : (
                ""
            );
        }, [
            CanShowDisputeIcon
        ]);

    const getGraudatedTiers =
        useCallback(
            (
                graduatedInfo: any[]
            ) => {
                return (
                    graduatedInfo?.map(
                        (
                            info: any
                        ) => ({
                            tierName:
                                info?.targetTierName,
                            tierId:
                                info?.targetTierId,
                            tierLevel:
                                info?.targetTierLevel,
                            measureNumber:
                                info?.targetMeasureNumber,
                            measureId:
                                info?.targetMeasureId
                        })
                    ) || []
                );
            },
            []
        );

    const sortedMeasures =
        useMemo(() => {
            return [
                ...(
                    standard?.measures ||
                    []
                )
            ].sort(
                (
                    first,
                    second
                ) =>
                    alphaNumericSort(
                        first?.measureNumber,
                        second?.measureNumber
                    )
            );
        }, [
            standard?.measures
        ]);

    const handleReasonSidebarHide =
        useCallback(() => {
            resetReviewState();
        }, [
            resetReviewState
        ]);

    const handleReasonDialogHide =
        useCallback(() => {
            resetReviewState();
        }, [
            resetReviewState
        ]);

    const handleDialogSave =
        useCallback(() => {
            setReasonViewDialogVisible(
                false
            );

            setReasonSidebarVisible(
                true
            );
        }, []);

    const filteredGraduatedMeasures =
        useMemo(
            () =>
                graduatedMeasures?.filter(
                    (
                        item
                    ) =>
                        item.sourceMeasureId ===
                        currentReviewedMeasure
                            ?.measureId
                ),
            [
                graduatedMeasures,
                currentReviewedMeasure
                    ?.measureId
            ]
        );

    const selectedReasonIdsMemo =
        useMemo(
            () =>
                selectedReasons.map(
                    (
                        reasonId: any
                    ) =>
                        reasonId
                ),
            [
                selectedReasons
            ]
        );

    const graduatedTiersMemo =
        useMemo(
            () =>
                getGraudatedTiers(
                    graduatedMeasures ||
                        []
                ),
            [
                getGraudatedTiers,
                graduatedMeasures
            ]
        );

    const handleCommentsSidebarHide =
        useCallback(() => {
            setSidebarVisible(
                false
            );

            setCommentMeasureId(
                0
            );

            setCommentMeasureNumber(
                ""
            );

            setCommentLevel(
                ""
            );

            setIsSmeSave(
                false
            );

            setCurrentReviewedMeasure(
                null
            );

            setJustificationReasons(
                undefined
            );

            setSelectedReasons(
                []
            );

            setGraduatedReview(
                []
            );

            setUserReason(
                ""
            );

            setReasonLoadError(
                ""
            );
        }, []);

    return (
        <div>
            <h4>
                {standard.standardTitle} Measures{" "}
                {columnDisputeQuesTemplate()}
            </h4>

            <div ref={tableWrapperRef}>
                <DataTable
                    selectionMode="single"
                    selection={selectedRow}
                    dataKey="measureId"
                    value={sortedMeasures}
                    expandedRows={expandedRows}
                    rowExpansionTemplate={
                        questionTemplateWithCalculatedFields
                    }
                    onRowToggle={(event) =>
                        setExpandedRows(
                            event.data
                        )
                    }
                    onRowUnselect={() =>
                        setSelectedRow(
                            null
                        )
                    }
                    onRowSelect={(event) =>
                        setSelectedRow(
                            event.data
                        )
                    }
                    onRowMouseEnter={(
                        event
                    ) => {
                        const newId =
                            event.data
                                ?.measureId;

                        const newNumber =
                            event.data
                                ?.measureNumber;

                        setCommentMeasureId(
                            (
                                previous
                            ) =>
                                previous ===
                                newId
                                    ? previous
                                    : newId
                        );

                        setCommentMeasureNumber(
                            (
                                previous
                            ) =>
                                previous ===
                                newNumber
                                    ? previous
                                    : newNumber
                        );

                        setCommentLevel(
                            (
                                previous
                            ) =>
                                previous ===
                                "measure"
                                    ? previous
                                    : "measure"
                        );
                    }}
                    tableStyle={{
                        tableLayout:
                            "fixed",
                        width:
                            "100%"
                    }}
                    className="p-datatable-custom"
                >
                    <Column
                        expander
                        style={{
                            width:
                                "3rem"
                        }}
                        bodyClassName="text-center"
                        headerClassName="text-center"
                    />

                    <Column
                        field="measureNumber"
                        header="Number"
                        body={
                            measureNameTemplate
                        }
                        bodyClassName="text-center"
                        headerClassName="text-center"
                        style={{
                            width:
                                "6rem"
                        }}
                    />

                    <Column
                        field="measureTitle"
                        header="Title"
                        bodyClassName="text-center"
                        headerClassName="text-center"
                        style={{
                            width:
                                "18rem"
                        }}
                    />

                    <Column
                        field="numberQuestions"
                        body={
                            numberOfQuestionsTemplate
                        }
                        header="Number of Questions"
                        bodyClassName="text-center"
                        headerClassName="text-center"
                        style={{
                            width:
                                "8rem"
                        }}
                    />

                    <Column
                        field="numberQuestions"
                        body={
                            approvedCheckbox
                        }
                        header="Approval Status"
                        bodyClassName="text-center"
                        headerClassName="text-center"
                        style={{
                            width:
                                "12rem"
                        }}
                    />

                    <Column
                        field="Comments"
                        body={
                            addCommentButton
                        }
                        bodyClassName="text-center"
                        headerClassName="text-center"
                        style={{
                            width:
                                "10rem"
                        }}
                    />
                </DataTable>
            </div>

            {sidebarVisible && (
                <CommentsSidebar
                    key={`${commentMeasureId}_${isSmeSave}_${commentsSidebarKey}`}
                    sidebarVisible={
                        sidebarVisible
                    }
                    handleCommentsSidebarHide={
                        handleCommentsSidebarHide
                    }
                    commentProviderMPI={
                        commentProviderMPI
                    }
                    providerType={
                        providerType
                    }
                    commentMeasureId={
                        commentMeasureId
                    }
                    commentMeasureNumber={
                        commentMeasureNumber
                    }
                    commentLevel={
                        commentLevel
                    }
                    updatePerformanceAreasForComments={
                        updatePerformanceAreasForComments
                    }
                    providerAgencyName={
                        providerAgencyName
                    }
                    alertMessage={
                        isSmeSave
                            ? "If this Measure requires a SME review, leave a comment providing context based on your review."
                            : undefined
                    }
                    externalSave={
                        isSmeSave
                            ? saveSMEResponse
                            : undefined
                    }
                />
            )}

            {reasonSidebarVisible && (
                <GraduateJustificationSideBar
                    key={`${currentReviewedMeasure?.measureNumber}_${isSmeSave}_${reasonSidebarVisible}`}
                    header={`Provide Justification for ${currentReviewedMeasure?.measureNumber}`}
                    message={
                        reasonLoadError ||
                        "Please select at least one reason that this measure is “Not Approved.”​"
                    }
                    style={{
                        width:
                            "50vw"
                    }}
                    onHide={
                        handleReasonSidebarHide
                    }
                    visible={
                        reasonSidebarVisible
                    }
                    isReadOnly={
                        !canEditJustification
                    }
                    reasons={
                        JustificationReasons ||
                        []
                    }
                    selectedReasonIds={
                        selectedReasonIdsMemo
                    }
                    onReasonChange={
                        onReasonChange
                    }
                    onSave={
                        saveNotApprovedResponse
                    }
                    graduatedMeasures={
                        filteredGraduatedMeasures
                    }
                    graduatedReview={
                        graduatedReview
                    }
                    loading={
                        loadingReasons
                    }
                    mpiId={
                        mpi
                    }
                    reviewYear={
                        reviewYear
                    }
                />
            )}

            {JustificationReasons != null &&
                JustificationReasons.length >
                    0 &&
                reasonViewDialogVisible && (
                    <GraduatedJustificationDialog
                        header={`Failed Measure(${currentReviewedMeasure?.measureNumber}) Justification`}
                        message="This measure was marked as “Not Approved”​ for the following reason(s):"
                        style={{
                            width:
                                "25vw"
                        }}
                        onHide={
                            handleReasonDialogHide
                        }
                        visible={
                            reasonViewDialogVisible
                        }
                        isReadOnly={
                            !canEditJustification ||
                            (
                                canDisableDropdown ??
                                false
                            )
                        }
                        reasons={
                            JustificationReasons ||
                            []
                        }
                        selectedReasonIds={
                            selectedReasonIdsMemo
                        }
                        userReason={
                            userReason
                        }
                        onReasonChange={
                            onReasonChange
                        }
                        onUserReasonChange={
                            setUserReason
                        }
                        onSave={
                            handleDialogSave
                        }
                        loading={
                            loadingReasons
                        }
                        graduatedMeasureReviews={
                            graduatedReview
                        }
                        desiredTier={
                            appliedTier
                        }
                        graduatedTiers={
                            graduatedTiersMemo
                        }
                    />
                )}

            <OverlayPanel
                ref={overlay}
                showCloseIcon
                closeOnEscape
            >
                <div
                    style={{
                        display:
                            "flex",
                        flexDirection:
                            "column"
                    }}
                >
                    <strong>
                        Measure Scoring
                        Guidance
                    </strong>

                    <Divider />

                    <div>
                        <p>
                            <span
                                style={{
                                    fontWeight:
                                        "bold"
                                }}
                            >
                                {
                                    currentMeasure?.measureNumber
                                }
                            </span>
                            :{" "}
                            <SafeHtml
                                html={
                                    currentMeasure?.measureDescription ??
                                    ""
                                }
                            />
                        </p>
                    </div>

                    <div>
                        <div className="mb-3">
                            <strong>
                                Scoring
                                Guidance:{" "}
                            </strong>
                        </div>

                        {scoringLoading && (
                            <span>
                                Loading...
                            </span>
                        )}

                        {!scoringLoading && (
                            <div>
                                <SafeHtml
                                    html={
                                        scoringHtml
                                    }
                                />
                            </div>
                        )}
                    </div>
                </div>
            </OverlayPanel>
        </div>
    );
}